import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { assurancesAPI, BASE_URL } from '../services/api';
import { Pencil, Trash2, Save, Shield, Upload } from 'lucide-react';
import toast from 'react-hot-toast';


const AssurancesPage = () => {
    const [assurances, setAssurances] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newAssurance, setNewAssurance] = useState({ name: '', code: '', city: '', phone: '', email: '', logo: '' });
    const [editingAssurance, setEditingAssurance] = useState(null);
    const [availableLogos, setAvailableLogos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => {
        loadAssurances();
        loadLogos();
    }, []);

    const loadLogos = async () => {
        try {
            const res = await assurancesAPI.getLogos();
            setAvailableLogos(res.data.data || []);
        } catch (err) {
            console.error('Error loading logos:', err);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('logo', file);

        setUploading(true);
        try {
            const res = await assurancesAPI.uploadLogo(formData);
            toast.success('Logo ajouté à la liste');
            await loadLogos();
            // Automatically select the new logo
            setNewAssurance(prev => ({ ...prev, logo: res.data.filename }));
        } catch (err) {
            toast.error('Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    const loadAssurances = async () => {
        setLoading(true);
        try {
            const res = await assurancesAPI.list({ search });
            setAssurances(res.data.data || []);
        } catch { toast.error('Erreur de chargement'); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            loadAssurances();
        }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const handleSave = async () => {
        if (!newAssurance.name.trim()) return toast.error('Nom obligatoire');
        try {
            if (editingAssurance) {
                await assurancesAPI.update(editingAssurance.id, newAssurance);
                toast.success('Assurance modifiée');
                setEditingAssurance(null);
            } else {
                await assurancesAPI.create(newAssurance);
                toast.success('Assurance ajoutée');
            }
            setNewAssurance({ name: '', code: '', city: '', phone: '', email: '', logo: '' });
            loadAssurances();
        } catch { toast.error('Erreur'); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer cette assurance ?')) return;
        try {
            await assurancesAPI.delete(id);
            setAssurances(prev => prev.filter(a => a.id !== id));
            toast.success('Supprimé');
        } catch { toast.error('Impossible de supprimer'); }
    };

    return (
        <Layout title="Gestion des Assurances">
            <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary-800)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Shield size={22} /> Partenaires Assureurs
                </h2>

                <div className="card mb-4">
                    <div className="form-section-title">{editingAssurance ? '✏️ Modifier' : '➕ Nouvelle'} Assurance</div>
                    <div className="form-grid form-grid-2" style={{ marginBottom: '12px' }}>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Nom *</label>
                            <input className="form-control" placeholder="Nom de la compagnie" value={newAssurance.name}
                                onChange={e => setNewAssurance(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Code</label>
                            <input className="form-control" placeholder="Code entreprise" value={newAssurance.code}
                                onChange={e => setNewAssurance(p => ({ ...p, code: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Ville</label>
                            <input className="form-control" placeholder="Ville de l'agence" value={newAssurance.city}
                                onChange={e => setNewAssurance(p => ({ ...p, city: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Téléphone</label>
                            <input className="form-control" placeholder="N° de téléphone" value={newAssurance.phone}
                                onChange={e => setNewAssurance(p => ({ ...p, phone: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                            <label className="form-label">Email</label>
                            <input className="form-control" type="email" placeholder="Email de contact" value={newAssurance.email}
                                onChange={e => setNewAssurance(p => ({ ...p, email: e.target.value }))} />
                        </div>
                        <div className="form-group" style={{ margin: 0, display: 'flex', gap: '15px' }}>
                            <div style={{ flex: 1 }}>
                                <label className="form-label">Logo de l'assurance</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select className="form-control" style={{ flex: 1 }} value={newAssurance.logo || ''} onChange={e => setNewAssurance(p => ({ ...p, logo: e.target.value }))}>
                                        <option value="">(Aucun logo)</option>
                                        {availableLogos.map(logo => (
                                            <option key={logo} value={logo}>{logo}</option>
                                        ))}
                                    </select>
                                    <input type="file" id="logo-upload" hidden accept="image/*" onChange={handleLogoUpload} />
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm"
                                        style={{ height: '38px' }}
                                        onClick={() => document.getElementById('logo-upload').click()}
                                        title="Uploader un nouveau logo"
                                        disabled={uploading}
                                    >
                                        <Upload size={14} />
                                    </button>
                                </div>
                            </div>
                            {newAssurance.logo && (
                                <div style={{
                                    width: '60px', height: '60px',
                                    border: '1px solid var(--border)',
                                    borderRadius: '8px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: '#fff',
                                    padding: '4px',
                                    marginTop: 'auto'
                                }}>
                                    <img src={`${BASE_URL}/assurances_logo/${newAssurance.logo}`} alt="Preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn-primary btn-sm" onClick={handleSave}>
                            <Save size={13} /> {editingAssurance ? 'Mettre à jour' : 'Ajouter'}
                        </button>
                        {editingAssurance && (
                            <button className="btn btn-ghost btn-sm" onClick={() => { setEditingAssurance(null); setNewAssurance({ name: '', code: '', city: '', phone: '', email: '', logo: '' }); }}>
                                Annuler
                            </button>
                        )}
                    </div>
                </div>

                <div className="table-container">
                    <div style={{ padding: '16px', borderBottom: '1px solid var(--border)' }}>
                        <div className="search-box" style={{ maxWidth: '300px' }}>
                            <input 
                                type="text" placeholder="Rechercher par nom ou code..." 
                                value={search} onChange={e => setSearch(e.target.value)} 
                            />
                            <span className="search-icon">🔍</span>
                        </div>
                    </div>
                    {loading ? <div className="spinner" /> : (
                        <div className="table-responsive">
                            <table>
                                <thead><tr><th>Logo</th><th>Nom</th><th>Code</th><th>Ville</th><th>Téléphone</th><th>Email</th><th>Actions</th></tr></thead>
                                <tbody>
                                    {Array.isArray(assurances) && assurances.map((a, i) => (
                                        <tr key={a.id}>
                                            <td style={{ textAlign: 'center' }}>
                                                {a.logo ? (
                                                    <img src={`${BASE_URL}/assurances_logo/${a.logo}`} alt="Logo" style={{ height: '30px', maxWidth: '80px', objectFit: 'contain' }} />
                                                ) : <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>—</span>}
                                            </td>
                                            <td style={{ fontWeight: '600' }}>{a.name || '—'}</td>
                                            <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{a.code || '—'}</td>
                                            <td style={{ fontSize: '12px' }}>{a.city || '—'}</td>
                                            <td style={{ fontSize: '12px' }}>{a.phone || '—'}</td>
                                            <td style={{ fontSize: '12px' }}>{a.email || '—'}</td>
                                            <td>
                                                <div className="table-actions">
                                                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditingAssurance(a); setNewAssurance({ name: a.name, code: a.code || '', city: a.city || '', phone: a.phone || '', email: a.email || '', logo: a.logo || '' }); }}>
                                                         <Pencil size={13} />
                                                     </button>
                                                    <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(a.id)}>
                                                        <Trash2 size={13} color="var(--danger)" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {!loading && assurances.length === 0 && <div className="empty-state"><p>Aucune assurance enregistrée</p></div>}
                </div>
            </div>
        </Layout>
    );
};

export default AssurancesPage;
